import { toDisplayJson } from '@shared/utils';
import { getPieveWorkflowTestConfigTargets } from '@shared/pieve/getPieveWorkflowTestConfigTargets.js';
import { QUERY_PAGE_MODES } from '@shared/apps/contracts/runAppAction/pagination.contract.js';
import humanizeAiTier from '@/lib/workflows/humanizeAiTier.js';
import { getFlowActionLabel } from '@/lib/workflowFlowControlMeta.js';

const PAGE_MODE_LABELS = Object.freeze({ single: 'One page', all: 'All pages' });
const EVENT_LEVELS = Object.freeze(['info', 'warn', 'error']);

const withoutKeys = (value, keys) => Object.fromEntries(Object.entries(value || {}).filter(([key]) => !keys.includes(key)));
const select = (values, labelOf = (value) => value) => ({ type: 'select', options: values.map((value) => ({ value, label: labelOf(value) })) });

// An action argument's control, from its declared type: its fixed list as a Select, true/false as a switch.
const argumentControl = (arg) => {
  if (Array.isArray(arg.listValues) && arg.listValues.length > 0) {
    return { type: 'select', options: arg.listValues.map(({ value, label }) => ({ value, label: label || value })) };
  }

  if (arg.dataType === 'boolean') {
    return { type: 'switch' };
  }

  if (arg.dataType === 'number') {
    return { type: 'number' };
  }

  return arg.dataType === 'object' || arg.dataType === 'array' ? { type: 'json' } : { type: 'template' };
};

const initialFor = (control) => {
  if (control.type === 'select') {
    return control.options[0]?.value ?? '';
  }

  if (control.type === 'filter') {
    return { field: control.schema?.fields?.[0]?.key ?? '', operator: control.schema?.operators?.[0] ?? 'eq', value: '' };
  }

  if (control.type === 'sort') {
    return [{ field: Array.isArray(control.schema?.fields) ? (control.schema.fields[0] ?? '') : '', direction: 'asc' }];
  }

  return { switch: false, number: 0, json: {}, group: {} }[control.type] ?? '';
};

const entry = (key, label, control) => ({ key, label, control, initial: initialFor(control) });

const argumentsSpec = (schema) => {
  const entries = (schema?.arguments || []).map((arg) => entry(arg.key, arg.name || arg.key, argumentControl(arg)));

  return { entries, canAdd: true };
};

const querySpec = (schema) => {
  const query = schema?.query || {};
  const pageModes = query.page?.modes?.length ? query.page.modes : QUERY_PAGE_MODES;
  const page = {
    entries: [
      entry(
        'mode',
        'Mode',
        select(pageModes, (mode) => PAGE_MODE_LABELS[mode] || mode)
      ),
      entry('size', 'Page size', { type: 'number' })
    ],
    canAdd: false
  };

  return {
    entries: [...(query.filter || !schema ? [entry('filter', 'Filter', { type: 'filter', schema: query.filter || null })] : []), ...(query.sort || !schema ? [entry('sort', 'Sort', { type: 'sort', schema: query.sort || null })] : []), { ...entry('page', 'Pages', { type: 'group', spec: page }), initial: { mode: pageModes[0] } }],
    canAdd: false
  };
};

const flowControlSpec = ({ step, actions }) => {
  const hook = (key, label, actionValues) => {
    const notify = [entry('sendEmail', 'Send email', { type: 'switch' }), entry('emailTo', 'Email to', { type: 'template' }), entry('emitEvent', 'Raise event', { type: 'switch' }), entry('eventLevel', 'Event level', select(EVENT_LEVELS))];
    const spec = { entries: [...(actionValues.length ? [entry('action', 'Then', select(actionValues, getFlowActionLabel))] : []), ...notify], canAdd: true };

    return { ...entry(key, label, { type: 'group', spec }), initial: actionValues.length ? { action: actionValues[0] } : {} };
  };
  const hooks = [hook('onFailure', 'On failure', actions?.onFailure || []), hook('onSuccess', 'On success', [])];

  // "On no results" is only a list step's question.
  if (step?.type === 'action') {
    hooks.splice(1, 0, hook('onNoResults', 'On no results', actions?.onNoResults || []));
  }

  return { entries: hooks, canAdd: true };
};

/*
 * The settings a person can change for one step in a test, in the order the step uses them, each with the
 * controls it is edited with: an action's arguments and query from the action's own schema (fixed lists as
 * Selects), flow control and AI reasoning from the engine's own choices. Where no schema is known, rows are
 * typed from the value as built. Which settings there are is getPieveWorkflowTestConfigTargets, the list the API
 * holds a test to, so the window can show every test it is given.
 *
 * A `json` field is an object edited as rows (`spec`); a `text` field is one string (`control`). `target` is a
 * whole top-level field of the step, or `options.<key>`, one key of its options. The whole-options field leaves
 * out the keys that have a field of their own (`omitKeys`). `saved` is the value as built, as the field's text.
 */
const listWorkflowTestConfigFields = (step, setup = null) => {
  const options = step?.options && typeof step.options === 'object' ? step.options : {};
  const schema = (setup?.schemas || []).find(({ stepId }) => stepId === step?.stepId) || null;
  const { fields, optionKeys, otherOptions } = getPieveWorkflowTestConfigTargets(step);
  const json = (key, label, target, value, spec = null, extra = {}) => ({ key, label, kind: 'json', target, saved: toDisplayJson(value ?? {}), spec, ...extra });
  const text = (optionKey, label, control, fallback = '') => ({ key: optionKey, label, kind: 'text', target: `options.${optionKey}`, saved: String(options[optionKey] ?? fallback), control });
  const fieldByKey = {
    arguments: () => json('arguments', 'Arguments', 'arguments', step.arguments, argumentsSpec(schema)),
    query: () => json('query', 'Query', 'query', step.query, querySpec(schema)),
    fields: () => json('fields', 'Fields', 'fields', step.fields)
  };
  const optionFieldByKey = {
    code: () => text('code', 'Code', { type: 'code' }),
    promptTemplate: () => text('promptTemplate', 'Prompt', { type: 'template', multiline: true }),
    tier: () => text('tier', 'Reasoning', select(setup?.aiTiers?.length ? setup.aiTiers : ['medium'], humanizeAiTier), 'medium'),
    selectorCode: () => text('selectorCode', 'Condition code', { type: 'code' }),
    routeSelectorCode: () => text('routeSelectorCode', 'Routing code', { type: 'code' }),
    flowControl: () => json('flowControl', 'Flow control', 'options.flowControl', options.flowControl, flowControlSpec({ step, actions: setup?.flowControl }))
  };
  const ownOptionKeys = optionKeys.filter((key) => key !== 'flowControl');
  const otherOptionsField = otherOptions ? [json('options', 'Options', 'options', withoutKeys(options, optionKeys), null, optionKeys.length ? { omitKeys: optionKeys } : {})] : [];

  // Flow control last: it is about what happens after the step, not what the step does.
  return [...fields.map((key) => fieldByKey[key]()), ...ownOptionKeys.map((key) => optionFieldByKey[key]()), ...otherOptionsField, ...(optionKeys.includes('flowControl') ? [optionFieldByKey.flowControl()] : [])];
};

export default listWorkflowTestConfigFields;
